import React, { Fragment, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../../components/Navbar/Navbar";
import PageTitle from "../../components/pagetitle/PageTitle";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";
import YouTubeVideoSection from "../../components/YouTubeVideoSection";
import { getOrganisationVideoId } from "../../constants/organisationVideos";
import Logo from "../../images/logo2.png";

const PartnerSinglePage = () => {
  const { id } = useParams();
  const [organisation, setOrganisation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleProjects, setVisibleProjects] = useState(6);
  const projectsPerLoad = 6;

  useEffect(() => {
    const fetchOrganisation = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL || "https://api.caprover.sokaab.com/api"}/public/organisations/${id}`,
        );
        setOrganisation(response.data);
      } catch (err) {
        console.error("Error fetching organisation:", err);
        setError("Failed to load organisation details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrganisation();
  }, [id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const calculateProgress = (raised, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min(100, (raised / goal) * 100);
  };

  if (loading) {
    return (
      <Fragment>
        <Navbar Logo={Logo} hclass={"wpo-site-header-s3"} />
        <PageTitle pageTitle={"Partner Details"} pagesub={"Partner"} />
        <section className="section-padding">
          <div className="container">
            <div className="text-center py-5">
              <span
                className="spinner-border"
                role="status"
                style={{ color: "#00CC99" }}
              />
              <p className="mt-2">Loading organisation details...</p>
            </div>
          </div>
        </section>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  if (error || !organisation) {
    return (
      <Fragment>
        <Navbar Logo={Logo} hclass={"wpo-site-header-s3"} />
        <PageTitle pageTitle={"Partner Not Found"} pagesub={"Partner"} />
        <section className="section-padding">
          <div className="container">
            <div className="text-center py-5">
              <i className="fas fa-exclamation-circle fa-3x text-muted mb-3"></i>
              <p>{error || "Organisation not found"}</p>
              <Link to="/partners" className="theme-btn-s4">
                <span>Back to Partners</span>
              </Link>
            </div>
          </div>
        </section>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Navbar Logo={Logo} hclass={"wpo-site-header-s3"} />
      <PageTitle
        pageTitle={organisation.organisationName || "Partner Details"}
        pagesub={"Partner"}
      />

      <section className="section-padding">
        <div className="container">
          {/* Organisation Header */}
          <div className="row mb-5">
            <div className="col-lg-12">
              <div
                className="partner-header-card p-4 rounded-3"
                style={{ background: "#f8f9fa" }}
              >
                <div className="row align-items-center">
                  <div className="col-md-3 text-center mb-3 mb-md-0">
                    {organisation.organisationLogo ? (
                      <img
                        src={organisation.organisationLogo}
                        alt={organisation.organisationName}
                        className="rounded-circle"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                        style={{
                          width: "150px",
                          height: "150px",
                          background:
                            "linear-gradient(135deg, #00CC99 0%, #009977 100%)",
                          color: "#fff",
                          fontSize: "48px",
                          fontWeight: "bold",
                        }}
                      >
                        {organisation.organisationName
                          ?.charAt(0)
                          ?.toUpperCase() || "O"}
                      </div>
                    )}
                  </div>
                  <div className="col-md-9">
                    <h2 className="mb-2">{organisation.organisationName}</h2>
                    {organisation.address && (
                      <p className="text-muted mb-2">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        {[organisation.address, organisation.country]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                    {organisation.organisationBio && (
                      <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
                        {organisation.organisationBio}
                      </p>
                    )}
                    <div className="d-flex flex-wrap gap-2">
                      {organisation.phoneNumber && (
                        <a
                          href={`tel:${organisation.phoneNumber}`}
                          className="btn btn-outline-primary btn-sm"
                        >
                          <i className="fas fa-phone me-1"></i> Call
                        </a>
                      )}
                      {organisation.emailAddress && (
                        <a
                          href={`mailto:${organisation.emailAddress}`}
                          className="btn btn-outline-primary btn-sm"
                        >
                          <i className="fas fa-envelope me-1"></i> Email
                        </a>
                      )}
                      {organisation.websiteAddress && (
                        <a
                          href={organisation.websiteAddress}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary btn-sm"
                        >
                          <i className="fas fa-globe me-1"></i> Website
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="row mb-5">
            <div className="col-md-3 col-6 mb-3">
              <div
                className="stat-card text-center p-3 rounded-3"
                style={{ background: "#e8f5f1" }}
              >
                <h3 className="mb-1" style={{ color: "#00CC99" }}>
                  {organisation.stats?.totalProjects || 0}
                </h3>
                <p className="text-muted mb-0 small">Total Projects</p>
              </div>
            </div>
            <div className="col-md-3 col-6 mb-3">
              <div
                className="stat-card text-center p-3 rounded-3"
                style={{ background: "#e3f2fd" }}
              >
                <h3 className="mb-1" style={{ color: "#1976d2" }}>
                  {organisation.stats?.activeProjects || 0}
                </h3>
                <p className="text-muted mb-0 small">Active Projects</p>
              </div>
            </div>
            <div className="col-md-3 col-6 mb-3">
              <div
                className="stat-card text-center p-3 rounded-3"
                style={{ background: "#f3e5f5" }}
              >
                <h3 className="mb-1" style={{ color: "#7b1fa2" }}>
                  {formatCurrency(organisation.stats?.totalRaised)}
                </h3>
                <p className="text-muted mb-0 small">Total Raised</p>
              </div>
            </div>
            <div className="col-md-3 col-6 mb-3">
              <div
                className="stat-card text-center p-3 rounded-3"
                style={{ background: "#fff3e0" }}
              >
                <h3 className="mb-1" style={{ color: "#f57c00" }}>
                  {formatCurrency(organisation.stats?.totalGoal)}
                </h3>
                <p className="text-muted mb-0 small">Total Goal</p>
              </div>
            </div>
          </div>

          {/* Organisation Video Section - Videos mapped by organisation ID in constants/organisationVideos.js */}
          {getOrganisationVideoId(id) && (
            <div className="row mb-5">
              <div className="col-lg-10 col-12 mx-auto">
                <YouTubeVideoSection
                  videoUrl={getOrganisationVideoId(id)}
                  title={`About ${organisation.organisationName}`}
                  description="Watch to learn more about our mission, impact, and the communities we serve."
                  showTitle={true}
                />
              </div>
            </div>
          )}

          {/* Funding Progress */}
          {organisation.stats?.totalGoal > 0 && (
            <div className="row mb-5">
              <div className="col-12">
                <div
                  className="p-4 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <h5 className="mb-3">Overall Funding Progress</h5>
                  <div className="progress" style={{ height: "12px" }}>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{
                        width: `${calculateProgress(organisation.stats?.totalRaised, organisation.stats?.totalGoal)}%`,
                        backgroundColor: "#00CC99",
                      }}
                    />
                  </div>
                  <div className="d-flex justify-content-between mt-2">
                    <span className="small text-muted">
                      Raised:{" "}
                      <strong style={{ color: "#00CC99" }}>
                        {formatCurrency(organisation.stats?.totalRaised)}
                      </strong>
                    </span>
                    <span className="small text-muted">
                      Goal:{" "}
                      <strong>
                        {formatCurrency(organisation.stats?.totalGoal)}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Projects Section */}
          <div className="row">
            <div className="col-12">
              <h4 className="mb-4">
                Projects by {organisation.organisationName}
              </h4>
            </div>
          </div>

          {organisation.projects && organisation.projects.length > 0 ? (
            <div className="row">
              {organisation.projects
                .slice(0, visibleProjects)
                .map((project) => {
                  const progress = calculateProgress(
                    project.fundraised,
                    project.fundingGoal,
                  );
                  const isEnded =
                    project.endDate && new Date(project.endDate) < new Date();

                  return (
                    <div
                      className="col-lg-4 col-md-6 col-12 mb-4"
                      key={project.id}
                    >
                      <div className="causes-item position-relative h-100">
                        <div
                          className={`status-badge ${isEnded ? "completed" : "active"}`}
                        >
                          {isEnded ? "Ended" : "Active"}
                        </div>
                        <Link to={`/projects/${project.id}`}>
                          <div className="image">
                            {project.image ? (
                              <img
                                src={project.image}
                                alt={project.title}
                                style={{
                                  height: "200px",
                                  objectFit: "cover",
                                  width: "100%",
                                }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{
                                  height: "200px",
                                  background: "#e9ecef",
                                }}
                              >
                                <i className="fas fa-image fa-3x text-muted"></i>
                              </div>
                            )}
                          </div>
                        </Link>
                        <div className="content">
                          <span className="badge bg-secondary mb-2">
                            {project.category}
                          </span>
                          <h2>
                            <Link
                              to={`/projects/${project.id}`}
                              style={{ fontSize: "18px", fontWeight: "normal" }}
                            >
                              {project.title?.length > 40
                                ? project.title.substring(0, 40) + "..."
                                : project.title}
                            </Link>
                          </h2>
                          {project.countryRegion && (
                            <p className="text-muted small mb-2">
                              <i className="fas fa-map-marker-alt me-1"></i>
                              {[project.village, project.countryRegion]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          )}
                          <p className="mb-2">
                            <strong>
                              {formatCurrency(project.fundraised)}
                            </strong>
                            <span className="text-muted"> raised of </span>
                            <strong>
                              {formatCurrency(project.fundingGoal)}
                            </strong>
                          </p>
                          <div
                            className="progress mb-2"
                            style={{ height: "6px" }}
                          >
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{
                                width: `${Math.min(100, progress)}%`,
                                backgroundColor:
                                  progress >= 100 ? "#28a745" : "#00CC99",
                              }}
                            />
                          </div>
                          <div className="d-flex justify-content-between">
                            <span
                              className="small"
                              style={{ color: "#00CC99" }}
                            >
                              {progress.toFixed(0)}% funded
                            </span>
                            <Link
                              to={`/projects/${project.id}`}
                              className="small"
                              style={{ color: "#00CC99" }}
                            >
                              View Details →
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="row">
              <div className="col-12 text-center py-5">
                <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                <p className="text-muted">No active projects at the moment.</p>
              </div>
            </div>
          )}

          {/* Load More Button */}
          {organisation.projects &&
            visibleProjects < organisation.projects.length && (
              <div className="row mt-3 mb-4">
                <div className="col-12 text-center">
                  <button
                    onClick={() =>
                      setVisibleProjects((prev) => prev + projectsPerLoad)
                    }
                    className="btn"
                    style={{
                      backgroundColor: "#00CC99",
                      color: "#fff",
                      padding: "12px 30px",
                      borderRadius: "8px",
                      fontWeight: "600",
                    }}
                  >
                    Load More Projects (
                    {organisation.projects.length - visibleProjects} remaining)
                  </button>
                </div>
              </div>
            )}

          {/* Back Button */}
          <div className="row mt-4">
            <div className="col-12 text-center">
              <Link to="/partners" className="theme-btn-s4">
                <span>← Back to All Partners</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default PartnerSinglePage;
